import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Building,
  Database,
  Globe,
  Key,
  Loader2,
  Palette,
  Settings as SettingsIcon,
  User,
  Zap
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { OfflineFirstInvitationNotifications } from "../components/invitation/OfflineFirstInvitationNotifications";
import { BrandingEditor } from "../components/organization/BrandingEditor";
import { CreateOrganizationDialog } from "../components/organization/CreateOrganizationDialog";
import { OrganizationManageDialog } from "../components/organization/OrganizationManageDialog";
import { ApiKeysSection } from "../components/settings/ApiKeysSection";
import { DomainsSection } from "../components/settings/DomainsSection";
import { ScopingAiSettingsSection } from "../components/settings/ScopingAiSettingsSection";
import { WebhooksSection } from "../components/settings/WebhooksSection";
import { PhoneInput } from "../components/ui/phone-input";
import { useUser } from "../contextapi/UserContext";
import { useToast } from "../hooks/use-toast";
import { useUserPermissions } from "../hooks/useUserPermissions";
import {
  getUserOrganizations,
  leaveOrganization
} from "../services/organizationService";
import { getUserTeams, TeamWithDetails } from "../services/teamService";
import { logFeatureFlags, settingsFeatureFlags } from "../utils/featureFlags";
import { PhoneValidationResult } from "../utils/phoneValidation";
import { EnhancedProfileSettings } from "./EnhancedProfileSettings";

// Check if CRM Base is enabled via environment variable
const isCrmBaseEnabled = import.meta.env.VITE_CRM_BASE_ENABLED === 'true';

// Charlie feature flag — hides org/workspace creation buttons in hosted deployments
const isOrgCreationDisabled = import.meta.env.VITE_DISABLE_ORG_CREATION === 'true';

// Debug logging
console.log('Environment check:', {
  VITE_CRM_BASE_ENABLED: import.meta.env.VITE_CRM_BASE_ENABLED,
  isCrmBaseEnabled,
  allEnvVars: import.meta.env
});

// Create a fallback component for when CRM is not available
const DataModelNotAvailable: React.FC = () => (
  <div className="dashboard-card p-6">
    <h3 className="text-xl font-semibold mb-4">Data Model Settings</h3>
    <div className="text-center py-8">
      <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
      <p className="text-muted-foreground mb-2">
        {isCrmBaseEnabled
          ? "Data Model settings are not available. The CRM module may not be installed."
          : "CRM Base is not enabled. Data Model settings are not available."
        }
      </p>
      <p className="text-sm text-muted-foreground">
        {isCrmBaseEnabled
          ? "Please ensure the CRM Base module is properly installed."
          : "Set VITE_CRM_BASE_ENABLED=true in your environment to enable CRM features."
        }
      </p>
    </div>
  </div>
);

// Dynamically import DataModelSettings only when needed
const loadDataModelSettings = async () => {
  console.log('loadDataModelSettings called, isCrmBaseEnabled:', isCrmBaseEnabled);

  if (!isCrmBaseEnabled) {
    console.log('CRM Base not enabled, returning fallback component');
    return { default: DataModelNotAvailable };
  }

  try {
    // Use dynamic path construction to avoid Vite static analysis
    const basePath = "../../../../../features/crm_base/pages/settings/";
    const componentName = "DataModelSettings";
    const fullPath = basePath + componentName;

    console.log('Attempting to import from path:', fullPath);
    const module = await import(/* @vite-ignore */ fullPath);
    console.log('Successfully imported DataModelSettings:', module);
    return module;
  } catch (error) {
    console.error("CRM Base DataModelSettings not found:", error);
    return { default: DataModelNotAvailable };
  }
};

const DataModelSettings = React.lazy(loadDataModelSettings);

type SettingsTab =
  | "profile"
  | "organizations"
  | "branding"
  | "api-keys"
  | "webhooks"
  | "domains"
  | "data-model"
  | "debug"
  | "debug-offline"
  | "debug-profile"
  | "debug-settings";

interface Organization {
  id: string;
  name: string;
  description: string | null;
  tenant_type?: 'personal' | 'organization';
  created_at: string;
  created_by: string;
  updated_at: string;
  role: string;
}


const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const { permissions } = useUserPermissions();

  // Log feature flags on component mount for debugging
  useEffect(() => {
    logFeatureFlags();
  }, []);

  // Filter tabs based on permissions and feature flags
  const getAvailableTabs = () => {
    const allTabs = [
      {
        id: "profile" as SettingsTab,
        label: "Profile",
        icon: User,
        description: "Manage your personal information",
        requiresPermission: () => true, // Everyone can access profile
      },
      {
        id: "organizations" as SettingsTab,
        label: "Workspace",
        icon: Building,
        description: "Manage your workspace",
        requiresPermission: () => true, //!permissions?.isGuest,
      },
      {
        id: "branding" as SettingsTab,
        label: "Branding",
        icon: Palette,
        description: "Customize your organization's branding",
        requiresPermission: () => settingsFeatureFlags.branding && (permissions?.canEditBranding || false),
      },
      {
        id: "api-keys" as SettingsTab,
        label: "API Keys",
        icon: Key,
        description: "Manage API keys for programmatic access",
        requiresPermission: () => settingsFeatureFlags.apiKeys && !permissions?.isGuest,
      },
      {
        id: "webhooks" as SettingsTab,
        label: "Webhooks",
        icon: Zap,
        description: "Configure webhooks for event notifications",
        requiresPermission: () => settingsFeatureFlags.webhooks && !permissions?.isGuest,
      },
      {
        id: "domains" as SettingsTab,
        label: "Domains",
        icon: Globe,
        description: "Manage subdomain and custom domain settings",
        requiresPermission: () => settingsFeatureFlags.domains && !permissions?.isGuest,
      },
      // {
      //   id: "data-model" as SettingsTab,
      //   label: "Data Model",
      //   icon: Database,
      //   description: "Manage custom objects and data models",
      //   requiresPermission: () => settingsFeatureFlags.dataModel && isCrmBaseEnabled && !permissions?.isGuest,
      // },
    ];

    return allTabs.filter(tab => tab.requiresPermission());
  };

  const tabs = getAvailableTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <EnhancedProfileSettings />;
      case "organizations":
        return <OrganizationsSettings />;
      case "branding":
        return <BrandingSettings />;
      case "api-keys":
        return <ApiKeysSection />;
      case "webhooks":
        return <WebhooksSection />;
      case "domains":
        return <DomainsSection />;
      case "data-model":
        return (
          <React.Suspense fallback={<div className="dashboard-card p-6"><div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div><span className="ml-2">Loading Data Model settings...</span></div></div>}>
            <DataModelSettings />
          </React.Suspense>
        );
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="w-full p-6">

        {/* Offline-First Invitation Notifications */}
        <OfflineFirstInvitationNotifications />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
          {/* Sidebar Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="dashboard-card p-4 sticky top-6">
              <h3 className="font-semibold mb-4 flex items-center space-x-2">
                <SettingsIcon className="w-5 h-5" />
                <span>Settings</span>
              </h3>
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${activeTab === tab.id
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{tab.label}</p>
                          <p className="text-xs opacity-75 truncate">
                            {tab.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderTabContent()}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Profile Settings Component
const ProfileSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.user_metadata?.name || "",
    fullName: user?.user_metadata?.full_name || "",
    phone: user?.phone || "",
  });
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResult>({
    isValid: true,
    formatted: ""
  });

  // Load profile data from profiles table
  const loadProfileData = async () => {
    if (!user?.id) return;

    try {
      const { supabase } = await import("../utils/supabaseClient");

      // Try to get profile data from profiles table first
      const { data: profileData, error } = await supabase
        .rpc("get_user_profile", { user_id: user.id });

      if (!error && profileData && profileData.length > 0) {
        // Use profile data from profiles table (RPC returns array)
        const profile = profileData[0];
        setFormData({
          name: profile.username || "",
          fullName: profile.full_name || "",
          phone: profile.phone || "",
        });
      } else {
        // Fallback to auth user metadata if profiles table doesn't have data
        setFormData({
          name: user.user_metadata?.name || "",
          fullName: user.user_metadata?.full_name || "",
          phone: user.phone || "",
        });
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
      // Fallback to auth user metadata
      setFormData({
        name: user.user_metadata?.name || "",
        fullName: user.user_metadata?.full_name || "",
        phone: user.phone || "",
      });
    }
  };

  // Load profile data when user changes
  // useEffect(() => {
  //   if (user?.id) {
  //     loadProfileData();
  //   }
  // }, [user?.id]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);

      // Validate phone number if provided
      if (formData.phone && !phoneValidation.isValid) {
        toast({
          title: "Validation Error",
          description: "Please enter a valid phone number in international format.",
          variant: "destructive",
        });
        return;
      }

      // Import supabase client
      const { supabase } = await import("../utils/supabaseClient");

      // Update the profiles table with all profile information including phone
      const { error: profileError } = await supabase.rpc("update_user_profile", {
        user_id: user?.id,
        profile_name: formData.name,
        profile_full_name: formData.fullName,
        profile_phone: formData.phone || null, // Use null if phone is empty
      });

      if (profileError) {
        // Handle specific phone validation errors from Supabase
        if (profileError.message?.includes('Invalid phone number format') ||
          profileError.message?.includes('E.164')) {
          toast({
            title: "Phone Number Error",
            description: "Phone number must be in international format (e.g., +1234567890). Please check your phone number and try again.",
            variant: "destructive",
          });
          return;
        }

        console.error("Profile update failed:", profileError);
        throw profileError;
      }

      // Only update auth metadata (not phone) for basic user info
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          name: formData.name,
          full_name: formData.fullName,
        },
      });

      // Don't throw error if auth metadata update fails - profiles table is the primary source
      if (authError) {
        console.warn("Auth metadata update failed:", authError);
      }

      // Reload profile data to reflect changes
      // await loadProfileData();

      toast({
        title: "Profile updated",
        description: "Your profile information has been saved successfully to your profile.",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update failed",
        description: "Failed to update profile information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="dashboard-card p-6">
        <h3 className="text-xl font-semibold mb-4">Profile Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Full Name</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-muted/50"
              placeholder="Enter your email"
              value={user?.email || ""}
              disabled
            />
            <p className="text-xs text-muted-foreground mt-1">
              Email cannot be changed from here. Contact support if needed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <PhoneInput
              value={formData.phone}
              onChange={(formatted, validation) => {
                setPhoneValidation(validation);
                handleInputChange("phone", formatted);
              }}
              placeholder="Enter your phone number"
              className="w-full"
            />
          </div>
          {/* <div>
            <label className="block text-sm font-medium mb-2">Location</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Enter your location"
              value={formData.location}
              onChange={(e) => handleInputChange("location", e.target.value)}
            />
          </div> */}
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={loading}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{loading ? "Saving..." : "Save Changes"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Organizations Settings Component
const OrganizationsSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const { permissions } = useUserPermissions();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<TeamWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmLeave, setConfirmLeave] = useState<{ orgId: string, orgName: string, tenantType?: 'personal' | 'organization' } | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<'workspace' | 'organisation'>('workspace');

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load organizations
      console.log('[Settings] 🔍 Loading organizations...');
      const orgData = await getUserOrganizations();
      console.log('[Settings] ✅ Organizations loaded:', orgData);
      console.log('[Settings] 📊 Organizations data:', {
        count: orgData.organizations.length,
        organizations: orgData.organizations.map(org => ({
          id: org.id,
          name: org.name,
          tenant_type: org.tenant_type,
        })),
        membership: orgData.membership,
      });

      const mappedOrgs = orgData.organizations.map((org) => {
        const membership = orgData.membership.find(
          (m) => m.organisation_id === org.id
        );
        return {
          id: org.id,
          name: org.name,
          description: org.description,
          tenant_type: org.tenant_type,
          created_at: org.created_at,
          created_by: org.created_by,
          updated_at: org.updated_at,
          role: membership?.role || "member",
        };
      });

      console.log('[Settings] 📋 Mapped organizations:', mappedOrgs);
      setOrganizations(mappedOrgs);

      // Load teams
      const teamData = await getUserTeams();
      setTeams(teamData);
    } catch (error) {
      console.error("Error loading organizations and teams:", error);
      toast({
        title: "Error",
        description: "Failed to load organizations and teams",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveOrganization = async (orgId: string, orgName: string) => {
    // Find the organization to get its tenant_type
    const org = organizations.find(o => o.id === orgId);
    setConfirmLeave({
      orgId,
      orgName,
      tenantType: org?.tenant_type === 'personal' ? 'personal' : 'organization'
    });
  };

  const confirmLeaveOrganization = async () => {
    if (!confirmLeave) return;

    try {
      setLeaving(true);
      const isPersonal = confirmLeave.tenantType === 'personal';
      console.log(`[Settings] 🚪 ${isPersonal ? 'Deleting personal workspace' : 'Leaving organization'}:`, confirmLeave);

      const { supabase } = await import("../utils/supabaseClient");

      if (isPersonal) {
        // Personal workspace deletion logic
        const totalUserOrgs = organizations.length;
        const personalWorkspacesCount = organizations.filter(org => org.tenant_type === 'personal').length;
        console.log(`[Settings] User has ${totalUserOrgs} total workspaces, ${personalWorkspacesCount} personal`);

        if (totalUserOrgs <= 1 || personalWorkspacesCount <= 1) {
          toast({
            title: "Cannot Delete Workspace",
            description: "You cannot delete your only workspace. Please create another workspace first.",
            variant: "destructive",
          });
          return;
        }

        // Delete personal workspace using delete_workspace_with_cleanup
        console.log('[Settings] 🗑️ Deleting personal workspace');
        const { data: deleteResult, error: deleteError } = await supabase
          .rpc('delete_workspace_with_cleanup', {
            p_tenant_id: confirmLeave.orgId,
            p_user_id: user?.id
          });

        if (deleteError) {
          console.error('Error deleting personal workspace:', deleteError);
          throw deleteError;
        }

        console.log('Personal workspace deletion result:', deleteResult);

        if (deleteResult?.success) {
          toast({
            title: "Personal Workspace Deleted",
            description: `${confirmLeave.orgName} has been permanently deleted.`,
          });
        } else {
          throw new Error(deleteResult?.error || 'Personal workspace deletion failed');
        }
      } else {
        // Organization leave logic
        // Get member count for this organization
        const { count: memberCount, error: countError } = await supabase
          .from('base_organisation_members')
          .select('*', { count: 'exact', head: true })
          .eq('organisation_id', confirmLeave.orgId)
          .eq('is_active', true);

        if (countError) {
          console.error('Error checking member count:', countError);
          throw countError;
        }

        const totalMembers = memberCount || 0;
        console.log(`[Settings] Organization has ${totalMembers} active members`);

        // Check if user has only 1 organization total (use the same data source as the UI)
        const totalUserOrgs = organizations.length;
        console.log(`[Settings] User has ${totalUserOrgs} total organizations`);

        if (totalUserOrgs <= 1) {
          // User has only 1 organization - prevent deletion
          toast({
            title: "Cannot Delete Organization",
            description: "You cannot delete your only organization. Please create another organization first.",
            variant: "destructive",
          });
          return;
        }

        if (totalMembers <= 1) {
          // User is the last member - delete the organization instead
          console.log('[Settings] 🗑️ User is last member, deleting organization');

          // Use the comprehensive organization deletion function
          const { data: deleteResult, error: deleteError } = await supabase
            .rpc('delete_organization_comprehensive', {
              p_org_id: confirmLeave.orgId,
              p_user_id: user?.id
            });

          if (deleteError) {
            console.error('Error deleting organization:', deleteError);
            throw deleteError;
          }

          console.log('Organization deletion result:', deleteResult);

          if (deleteResult?.success) {
            toast({
              title: "Organization Deleted",
              description: `You were the last member of ${confirmLeave.orgName}. The organization has been deleted.`,
            });
          } else {
            throw new Error(deleteResult?.error || 'Organization deletion failed');
          }
        } else {
          // Normal leave process
          await leaveOrganization(confirmLeave.orgId);

          toast({
            title: "Success",
            description: `You have successfully left ${confirmLeave.orgName}`,
          });
        }
      }

      // Refresh the organizations list
      await loadData();

    } catch (error) {
      console.error("Error leaving/deleting:", error);
      const isPersonal = confirmLeave.tenantType === 'personal';
      toast({
        title: "Error",
        description: `Failed to ${isPersonal ? 'delete personal workspace' : 'leave organization'}. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setLeaving(false);
      setConfirmLeave(null);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-card p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading organizations and teams...</span>
        </div>
      </div>
    );
  }

  // Filter organizations by type
  // Debug: Log all tenant types to see what we're working with
  console.log('[Settings] 🏢 All organizations:', organizations.map(org => ({
    id: org.id,
    name: org.name,
    tenant_type: org.tenant_type,
  })));

  const personalWorkspaces = organizations.filter(org => org.tenant_type === 'personal');
  // tenant_type is normalized in the service, so we only need to check for 'organization'
  const organizationWorkspaces = organizations.filter(org => {
    const isOrg = org.tenant_type === 'organization';
    if (!isOrg) {
      console.log(`[Settings] ⚠️ Organization "${org.name}" filtered out: tenant_type="${org.tenant_type}"`);
    }
    return isOrg;
  });

  console.log('[Settings] 📊 Filtered results:', {
    totalOrganizations: organizations.length,
    personalCount: personalWorkspaces.length,
    organizationCount: organizationWorkspaces.length,
    personalWorkspaces: personalWorkspaces.map(org => ({ name: org.name, type: org.tenant_type })),
    organizationWorkspaces: organizationWorkspaces.map(org => ({ name: org.name, type: org.tenant_type })),
  });

  // Render organization list
  const renderOrganizationList = (orgs: Organization[], emptyMessage: string) => {
    if (orgs.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {orgs.map((org) => (
          <div key={org.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Building className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">{org.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {org.role} • Created{" "}
                    {new Date(org.created_at).toLocaleDateString()}
                  </p>
                  {org.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {org.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {permissions?.isOrgAdmin ? (
                  <OrganizationManageDialog
                    organization={org}
                    onSuccess={loadData}
                    onDelete={loadData}
                  />
                ) : (
                  <button
                    className="px-3 py-1 text-sm bg-muted text-muted-foreground rounded cursor-not-allowed"
                    disabled
                    title="View only - Admin access required to manage"
                  >
                    View Only
                  </button>
                )}
                {org.role !== "org_admin" && (
                  <button
                    onClick={() => handleLeaveOrganization(org.id, org.name)}
                    className="px-3 py-1 text-sm text-destructive hover:bg-destructive/10 rounded transition-colors"
                  >
                    Leave
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="dashboard-card p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold">Workspaces</h3>
        </div>

        {/* Tabs for Personal and Organisation */}
        <div className="mb-6">
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveWorkspaceTab('workspace')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeWorkspaceTab === 'workspace'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
              >
                Personal ({personalWorkspaces.length})
              </button>
              <button
                onClick={() => setActiveWorkspaceTab('organisation')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeWorkspaceTab === 'organisation'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
              >
                Organisation ({organizationWorkspaces.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeWorkspaceTab === 'workspace' ? (
          <div className="space-y-4">
            {!isOrgCreationDisabled && (
              <div className="flex justify-end">
                <CreateOrganizationDialog
                  onSuccess={loadData}
                  buttonText="Create Personal Workspace"
                  tenantType="personal"
                  dialogTitle="Create Personal Workspace"
                  dialogDescription="Set up a new personal workspace"
                />
              </div>
            )}
            {renderOrganizationList(personalWorkspaces, "You haven't created any personal workspaces yet.")}
          </div>
        ) : (
          <div className="space-y-4">
            {!isOrgCreationDisabled && (
              <div className="flex justify-end">
                <CreateOrganizationDialog
                  onSuccess={loadData}
                  buttonText="Create Organisation"
                  tenantType="organisation"
                  dialogTitle="Create Organisation"
                  dialogDescription="Set up a new organization workspace"
                />
              </div>
            )}
            {renderOrganizationList(organizationWorkspaces, "You haven't joined any organizations yet.")}
          </div>
        )}
      </div>

      {/* Leave Organization Confirmation Dialog */}
      {confirmLeave && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setConfirmLeave(null)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:  sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {confirmLeave.tenantType === 'personal' ? 'Delete Personal Workspace' : 'Leave Organization'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {confirmLeave.tenantType === 'personal'
                          ? <>Are you sure you want to delete <strong>{confirmLeave.orgName}</strong>? This action cannot be undone.</>
                          : <>Are you sure you want to leave <strong>{confirmLeave.orgName}</strong>?</>
                        }
                      </p>
                      {confirmLeave.tenantType === 'organization' && (
                        <p className="text-sm text-gray-500 mt-2">
                          If you are the last member, the organization will be permanently deleted.
                        </p>
                      )}
                      {confirmLeave.tenantType === 'personal' && (
                        <p className="text-sm text-gray-500 mt-2">
                          All data in this personal workspace will be permanently deleted.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmLeaveOrganization}
                  disabled={leaving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {leaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {confirmLeave.tenantType === 'personal' ? 'Deleting...' : 'Leaving...'}
                    </>
                  ) : (
                    confirmLeave.tenantType === 'personal' ? 'Delete Personal Workspace' : 'Leave Organization'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmLeave(null)}
                  disabled={leaving}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Branding Settings Component
const BrandingSettings: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadOrganizations();
    }
  }, [user?.id]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const orgData = await getUserOrganizations();
      const userOrgs = orgData.organizations.map((org) => {
        const membership = orgData.membership.find(
          (m) => m.organisation_id === org.id
        );
        return {
          id: org.id,
          name: org.name,
          description: org.description,
          created_at: org.created_at,
          created_by: org.created_by,
          updated_at: org.updated_at,
          role: membership?.role || "member",
        };
      });

      setOrganizations(userOrgs);

      // Auto-select the first organization if available
      if (userOrgs.length > 0) {
        setSelectedOrg(userOrgs[0]);
      }
    } catch (error) {
      console.error("Error loading organizations:", error);
      toast({
        title: "Error",
        description: "Failed to load organizations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-card p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Loading organizations...</span>
        </div>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="dashboard-card p-6">
        <div className="text-center py-8">
          <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">No Organizations Found</h3>
          <p className="text-muted-foreground mb-4">
            You need to create or join an organization to manage branding
            settings.
          </p>
          <p className="text-sm text-muted-foreground">
            Go to the Organizations & Teams tab to create your first
            organization.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Organization Selection */}
      {organizations.length > 1 && (
        <div className="dashboard-card p-6">
          <h3 className="text-lg font-semibold mb-4">Select Organization</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => setSelectedOrg(org)}
                className={`p-4 border rounded-lg text-left transition-all ${selectedOrg?.id === org.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Building className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium">{org.name}</h4>
                    <p className="text-sm text-muted-foreground">{org.role}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Branding Editor */}
      {selectedOrg && (
        <div className="dashboard-card p-6">
          <BrandingEditor
            organization={selectedOrg}
            onSuccess={() => {
              toast({
                title: "Success",
                description: "Branding settings updated successfully",
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
